import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Terminal } from "lucide-react";
import { API, BACKEND } from "@/lib/api";


const inputCls =
    "w-full rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2.5 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none";

const timeAgo = (iso) => {
    if (!iso) return "never";
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

export default function ApiTokensTab() {
    const [tokens, setTokens] = useState(null);
    const [label, setLabel] = useState("");
    const [scope, setScope] = useState("read");
    const [fresh, setFresh] = useState(null);
    const [busy, setBusy] = useState(false);
    const [revoking, setRevoking] = useState("");

    const load = useCallback(async () => {
        try {
            const r = await fetch(`${API}/auth/tokens`, { credentials: "include" });
            if (!r.ok) throw new Error("failed");
            setTokens((await r.json()).tokens || []);
        } catch {
            toast.error("Could not load API tokens");
            setTokens([]);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const create = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/auth/tokens`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ label, scope }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            setFresh(d.token);
            setLabel("");
            setScope("read");
            toast.success("Token created — copy it now");
            load();
        } catch (e) {
            toast.error("Could not create token", { description: String(e.message) });
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (id) => {
        setRevoking(id);
        try {
            const r = await fetch(`${API}/auth/tokens/${id}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!r.ok) throw new Error("failed");
            toast.success("Token revoked");
            load();
        } catch {
            toast.error("Could not revoke token");
        } finally {
            setRevoking("");
        }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const ta = document.createElement("textarea");
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            ta.remove();
        }
        toast.success("Copied");
    };

    return (
        <div data-testid="settings-api-tab" className="space-y-4">
            <div className="glass-panel rounded-xl p-6">
                <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                    Personal access tokens
                </h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-400">
                    Use a token instead of your email and password when scripts, CI, or agents call
                    the MDH API. Send it as a Bearer header — no login flow, no cookies.
                </p>

                <div className="mt-5 flex max-w-xl flex-wrap items-end gap-2">
                    <label className="block min-w-44 flex-1">
                        <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Label
                        </span>
                        <input
                            data-testid="token-label-input"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. donation-script"
                            maxLength={100}
                            className={inputCls}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Scope
                        </span>
                        <select
                            data-testid="token-scope-select"
                            value={scope}
                            onChange={(e) => setScope(e.target.value)}
                            className={inputCls}
                        >
                            <option value="read">read — view only</option>
                            <option value="write">write — read + change</option>
                            <option value="admin">admin — full access</option>
                        </select>
                    </label>
                    <button
                        data-testid="token-create-button"
                        onClick={create}
                        disabled={busy}
                        className="shrink-0 rounded-md bg-cyan-400 px-5 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                    >
                        {busy ? "Creating…" : "Create token"}
                    </button>
                </div>
                <p className="mt-2 font-mono text-[10px] text-slate-600">
                    read = GET only · write = no deletes · admin = everything. Tokens can never
                    manage other tokens.
                </p>

                {fresh && (
                    <div
                        data-testid="token-reveal-box"
                        className="mt-4 rounded-lg border border-amber-500/40 bg-amber-950/20 p-4"
                    >
                        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-300">
                            Copy this token now — it will never be shown again
                        </p>
                        <div className="mt-2.5 flex items-center gap-2">
                            <code className="min-w-0 flex-1 break-all rounded border border-slate-800 bg-slate-950/80 px-3 py-2 font-mono text-[11px] text-cyan-200">
                                {fresh}
                            </code>
                            <button
                                data-testid="token-copy-button"
                                onClick={() => copy(fresh)}
                                className="shrink-0 rounded-md border border-cyan-500/40 bg-cyan-950/40 p-2 text-cyan-300 transition-colors hover:bg-cyan-950/70"
                                aria-label="Copy token"
                            >
                                <Copy className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                )}

                <div data-testid="token-list" className="mt-5 space-y-2.5">
                    {tokens === null ? (
                        <p className="font-mono text-[11px] text-slate-600">Loading…</p>
                    ) : tokens.filter((t) => !t.revoked).length === 0 ? (
                        <p
                            data-testid="token-empty-state"
                            className="font-mono text-[11px] text-slate-600"
                        >
                            No tokens yet — create one above.
                        </p>
                    ) : (
                        tokens
                            .filter((t) => !t.revoked)
                            .map((t) => (
                                <div
                                    key={t.token_id}
                                    data-testid={`token-item-${t.token_id}`}
                                    className="flex items-center gap-3.5 rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-3"
                                >
                                    <KeyRound className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                                    <div className="min-w-0 flex-1">
                                        <span className="text-xs font-semibold text-slate-200">
                                            {t.label || "Unlabeled"}
                                        </span>
                                        <span
                                            data-testid={`token-scope-${t.token_id}`}
                                            className={`ml-2 rounded border px-1.5 py-px font-mono text-[9px] uppercase tracking-wider ${
                                                t.scope === "admin"
                                                    ? "border-rose-500/30 bg-rose-950/40 text-rose-300"
                                                    : t.scope === "write"
                                                      ? "border-amber-500/30 bg-amber-950/40 text-amber-300"
                                                      : "border-slate-700 text-slate-500"
                                            }`}
                                        >
                                            {t.scope || "read"}
                                        </span>
                                        <p className="mt-0.5 font-mono text-[10px] text-slate-500">
                                            {t.prefix} · created {timeAgo(t.created_at)} · last used{" "}
                                            {timeAgo(t.last_used_at)}
                                        </p>
                                    </div>
                                    <button
                                        data-testid={`token-revoke-${t.token_id}`}
                                        onClick={() => revoke(t.token_id)}
                                        disabled={revoking === t.token_id}
                                        className="shrink-0 rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:border-rose-500/50 hover:text-rose-300 disabled:opacity-50"
                                    >
                                        {revoking === t.token_id ? "…" : "Revoke"}
                                    </button>
                                </div>
                            ))
                    )}
                </div>
            </div>

            <div className="glass-panel rounded-xl p-6">
                <div className="flex items-center gap-2">
                    <Terminal className="h-3.5 w-3.5 text-cyan-400" />
                    <h3 className="font-display text-sm font-bold tracking-tight text-slate-100">
                        Usage
                    </h3>
                </div>
                <p className="mt-1.5 text-xs text-slate-400">
                    Base URL: <code className="font-mono text-[11px] text-cyan-300">{BACKEND}</code>
                </p>
                <pre
                    data-testid="token-usage-example"
                    className="mt-3 overflow-x-auto rounded-lg border border-slate-800/80 bg-slate-950/70 p-4 font-mono text-[11px] leading-relaxed text-slate-300"
                >{`curl -H "Authorization: Bearer mdh_pat_…" \\
  ${BACKEND}/api/auth/me

curl -H "Authorization: Bearer mdh_pat_…" \\
  ${BACKEND}/api/apps

# live event stream (scripts/CI — browsers use cookies automatically)
wss://${BACKEND.replace(/^https?:\/\//, "")}/api/ws/apps/{app_id}?token=mdh_pat_…`}</pre>
                <p className="mt-3 font-mono text-[10px] leading-relaxed text-slate-500">
                    Treat tokens like passwords — keep them in environment variables or a secret
                    manager, never in source code. Revoke immediately if one leaks.
                </p>
            </div>
        </div>
    );
}
